import FormError from '@Components/FormError/FormError';
import ModalWrapper from '@Components/Modals/ModalWrapper/ModalWrapper';
import AccessoriesServices from '@Services/Accessories.services.ts';
import AssetServices from '@Services/Asset.services.ts';
import { AssetAssignFormFields, MultiDropOption } from '@Types/Form.types';
import { DROPDOWN_DEFAULT } from '@Utils/constants';
import { formatRequired } from '@Utils/form.utils';
import { formatAccessoriesDropdown, formatAssetsDropdown, formatPeopleDropdown } from '@Utils/format.utils.tsx';
import { handleRequestError } from '@Utils/request.utils';
import { VALIDATION_RULES } from '@Utils/rule.utils.ts';
import { useApolloClient } from '@apollo/client';
import { Dropdown, ErrorDisplay, LogoLoader, MultiSelectDropdown, TextArea, Toast } from '@kg-internal/pv3-uikit';
import { AxiosResponse } from 'axios';
import cx from 'classnames';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { GetEmployeeByEmailDocument, GetEmployeeByEmailQuery, useGetEmployeesQuery } from '~/__generated__/graph';
import { getUserWithLocation } from '~/Utils/user.utils';

type Props = {
  assetId?: string;
  showModal: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

const { ASSIGN_OWNER } = VALIDATION_RULES;

const AssetAssignModal = ({ assetId, showModal, onClose, onSuccess }: Props) => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    register,
  } = useForm<AssetAssignFormFields>({
    defaultValues: {
      employeeEmail: DROPDOWN_DEFAULT.value,
      description: '',
    },
  });
  const [selectedAssets, setSelectedAssets] = useState<MultiDropOption[]>([]);
  const [selectedAccessories, setSelectedAccessories] = useState<MultiDropOption[]>([]);
  const [assetOptions, setAssetOptions] = useState<MultiDropOption[]>([]);
  const [accessoryOptions, setAccessoryOptions] = useState<MultiDropOption[]>([]);
  const [emptySelectionError, setEmptySelectionError] = useState<string>('');
  const resetDropdowns = () => {
    setSelectedAssets([]);
    setSelectedAccessories([]);
  };
  const client = useApolloClient();

  const onSubmitSuccess = () => {
    onSuccess();
    reset();
    resetDropdowns();
    onClose();
  };

  /**
   * Generic method to assign assets or accessories to an user
   *
   * @param {string[]} ids - Ids of assets/accessories to assign
   * @param {string} email - Employee email of the future owner
   * @param {(id: string, { employeeEmail }: { employeeEmail: string }) => Promise<AxiosResponse>} method - Service method to assign asset/accessory
   * @returns {boolean} - Specifies if there was an error in the call
   */
  const assign = async (
    ids: string[],
    user: { employeeEmail: string; employeeLocation: string },
    method: (id: string, { employeeEmail }: { employeeEmail: string }) => Promise<AxiosResponse>,
  ) => {
    let hasError = false;
    const promises = ids.map((id: string) =>
      method(id, user).catch((err) => {
        hasError = true;
        handleRequestError(err, setErrorMessage, true);
      }),
    );
    await Promise.all(promises);
    return hasError;
  };

  const handleAssignSubmit = async (inputData: AssetAssignFormFields) => {
    const { data: kgUser, error: portalError } = await client.query<GetEmployeeByEmailQuery>({
      query: GetEmployeeByEmailDocument,
      variables: { email: inputData.employeeEmail },
    });

    if (portalError || !kgUser.person) {
      Toast.warning('Could not fetch employee.');
    } else {
      const userWithLocation = getUserWithLocation(kgUser.person);

      let hasError = false;
      if (assetId) {
        hasError = await assign([assetId], userWithLocation, AssetServices.assignAsset);
      } else if (!selectedAssets.length && !selectedAccessories.length) {
        Toast.warning('Please select at least one asset or accessory.');
      } else {
        if (selectedAssets.length) {
          const assetIds = selectedAssets.map((asset) => asset.value);
          hasError = await assign(assetIds, userWithLocation, AssetServices.assignAsset);
        }

        if (!hasError && selectedAccessories.length) {
          const accessoryIds = selectedAccessories.map((accessory) => accessory.value);
          hasError = await assign(accessoryIds, userWithLocation, AccessoriesServices.assignAccessory);
        }
      }
      if (!hasError) {
        Toast.success('Asset(s) assigned successfully.');
        onSubmitSuccess();
      }
    }
  };

  const { loading, error, data } = useGetEmployeesQuery();

  useEffect(() => {
    Promise.all([AssetServices.getAssets(null, false), AccessoriesServices.getAccessories(null, false)]).then(
      ([assets, accessories]) => {
        setAssetOptions(formatAssetsDropdown(assets.data.docs));
        setAccessoryOptions(formatAccessoriesDropdown(accessories.data.docs));
      },
    );
  }, []);

  useEffect(() => setEmptySelectionError(''), [selectedAssets, selectedAccessories]);

  if (loading) {
    return (
      <div className='modal--pt-25vh modal--fit-content'>
        <ModalWrapper
          title='Assign Asset'
          show={showModal}
          handleClose={onClose}
          disablePrimary
          primaryButtonText='Confirm'>
          <LogoLoader inline />
        </ModalWrapper>
      </div>
    );
  }

  if (error) {
    setErrorMessage(error.message);
  }

  const validateFields = () => {
    if (!selectedAssets.length && !selectedAccessories.length) {
      setEmptySelectionError('Please select at least one asset or accessory.');
    }
  };

  const getForm = () => (
    <form>
      <div className='grid'>
        <div className='col-12 col-md-6'>
          <span
            className={cx({
              'form-error': errors.employeeEmail?.message,
            })}>
            <Dropdown.RHF
              options={formatPeopleDropdown(data?.peopleV2)}
              label={formatRequired('Owner')}
              name='employeeEmail'
              control={control}
              rules={ASSIGN_OWNER.OWNER}
              id='employeeEmail'
              fullWidth
            />
            <FormError error={errors.employeeEmail?.message} />
          </span>
          {!assetId && (
            <TextArea
              label={formatRequired('Notes')}
              {...register('description', ASSIGN_OWNER.NOTES)}
              error={!!errors.description}
              errorMessage={errors.description?.message}
              rows={4}
            />
          )}
        </div>
        <div className='col-12 col-md-6'>
          {assetId ? (
            <TextArea
              label={formatRequired('Notes')}
              {...register('description', ASSIGN_OWNER.NOTES)}
              error={!!errors.description}
              errorMessage={errors.description?.message}
              rows={4}
            />
          ) : (
            <span
              className={cx({
                'form-error': emptySelectionError,
              })}>
              <MultiSelectDropdown<string>
                options={assetOptions}
                values={selectedAssets}
                selectOption={(selected) => {
                  setSelectedAssets((assets) => [...assets, selected]);
                }}
                removeOption={(removed) =>
                  setSelectedAssets((assets) => assets.filter((asset) => asset.value !== removed.value))
                }
                fullWidth
                label='Assets'
                value={DROPDOWN_DEFAULT}
              />

              <MultiSelectDropdown<string>
                options={accessoryOptions}
                values={selectedAccessories}
                selectOption={(selected) => {
                  setSelectedAccessories((accessories) => [...accessories, selected]);
                }}
                removeOption={(removed) =>
                  setSelectedAccessories((accessories) =>
                    accessories.filter((accessory) => accessory.value !== removed.value),
                  )
                }
                fullWidth
                label='Accessories'
                // neccesary to reset the default value, otherwise it shows the last selected value even with an empty values array
                value={DROPDOWN_DEFAULT}
              />
              <FormError error={emptySelectionError} />
            </span>
          )}
        </div>
      </div>
    </form>
  );
  return (
    <div className={`modal--pt-${!assetId ? '25' : '30'}vh modal--fit-content`}>
      <ModalWrapper
        title='Assign Asset'
        primaryButtonText='Confirm'
        show={showModal}
        handleClose={onClose}
        primaryAction={() => {
          validateFields();
          handleSubmit(handleAssignSubmit)();
        }}>
        {errorMessage ? <ErrorDisplay inline details={errorMessage} /> : getForm()}
      </ModalWrapper>
    </div>
  );
};

export default AssetAssignModal;
